<template>
  <component
    :is="tag"
    :id="elementId"
    :class="classes"
    role="menuitem"
    tabindex="0"
    :to="to"
    :aria-disabled="disabled"
    @click="handleClick"
    @keydown="$emit('keydown', $event)"
  >
    <span v-if="icon || $slots.icon" class="sb-dropdown-item__icon-wrapper">
      <slot name="icon" />
      <SbIcon
        v-if="!$slots.icon"
        class="sb-dropdown-item__icon"
        :name="icon"
        :color="iconColor"
        size="normal"
      />
    </span>
    <span class="sb-dropdown-item__content">
      <span v-if="label">
        <slot name="label" />
        <template v-if="!$slots.label">
          {{ label }}
        </template>
      </span>
      <slot />
      <span
        v-if="description"
        class="sb-dropdown-item__description"
        :aria-hidden="hideDescription"
      >
        <slot name="description" />
        <template v-if="!$slots.description">
          {{ description }}
        </template>
      </span>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { randomString } from '../../utils'
import SbIcon from '../Icon/SbIcon.vue'
import type { IconColor, IconName } from '../Icon'

export type DropdownItemProps = {
  /**
   * Define if a dropdown item is disabled or not
   */
  disabled?: boolean

  /**
   * Define if a dropdown item has a danger action. This property will change the dropdown item styles
   */
  negative?: boolean

  /**
   * Define a description for the dropdown item. It will be displayed below the label
   */
  description?: string

  /**
   * Define a label for the dropdown item. It will be displayed as the main content
   */
  label?: string

  /**
   * Define an icon for the dropdown item. It will be displayed on the left side of the label
   */
  icon?: IconName

  /**
   * Define the color of the icon. Default is 'primary'
   */
  iconColor?: IconColor

  /**
   * Define the role of the dropdown item. Default is 'menuitem'
   */
  role?: string

  /**
   * Define the tag of the dropdown item. Default is 'li'
   */
  tag?: string

  /**
   * Define the to attribute for the dropdown item. Should be used along with router-link
   */
  to?: Record<string, unknown>

  /**
   * Define if the description should be hidden from accessibility tools. Default is false
   */
  hideDescription?: boolean
}

const props = withDefaults(defineProps<DropdownItemProps>(), {
  description: undefined,
  label: undefined,
  icon: undefined,
  iconColor: 'primary',
  role: 'menuitem',
  tag: 'li',
  to: undefined,
})
const emit = defineEmits(['click', 'keydown'])

const classes = computed(() => {
  return {
    'sb-dropdown-item': true,
    'sb-dropdown-item--disabled': props.disabled,
    'sb-dropdown-item--negative': props.negative,
  }
})

const elementId = computed(() => `sb-dropdown-item-${randomString(4)}`)

const handleClick = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('click', event)
}
</script>
